import { assign, fromPromise, setup } from 'xstate';
import { openDeleteConfirmation, sendCustomError, showAxiosErrorAction } from '../../actions';
import {
  addAOI,
  clearSubmitError,
  removeAoi,
  setAoiBuffer,
  setAOIOptions,
  setAois,
  setIdToDelete,
  setLoadingFalse,
  setLoadingTrue,
  setProviderIds,
  setSpecialtiesId,
  setSpecOptions,
  setSubmitError,
  toggleModal,
} from './actions';
import { AOI_API_CALLS, AOI_OPTIONS_CALLS, AOI_SPEC_OPTIONS_CALLS } from './api';
import { emptyForm } from './constants';
import { Events, SubmitAOIEvent } from './events';
import { Context } from './types';

export const aoiMachine = setup({
  actors: {
    get: fromPromise(AOI_API_CALLS.get),
    create: fromPromise(AOI_API_CALLS.create),
    delete: fromPromise(AOI_API_CALLS.delete),
    getSpecOptions: fromPromise(AOI_SPEC_OPTIONS_CALLS.get),
    getAOIOptions: fromPromise(AOI_OPTIONS_CALLS.get),
  },
  types: {
    context: {} as Context,
    events: {} as Events,
    input: {} as Partial<Context>,
  },
  actions: {
    setLoadingTrue: setLoadingTrue,
    setLoadingFalse: setLoadingFalse,
    sendError: showAxiosErrorAction,
    setAois: assign(setAois),
    setIdToDelete: assign(setIdToDelete),
    add: assign(addAOI),
    delete: assign(removeAoi),
    setProviderIds: assign(setProviderIds),
    setSpecOptions: assign(setSpecOptions),
    setSpecialtiesId: assign(setSpecialtiesId),
    setAOIOptions: assign(setAOIOptions),
    toggleModal: assign(toggleModal),
    openConfirmation: openDeleteConfirmation,
    setAoiBuffer: assign(setAoiBuffer),
    // [MCV-1207] Inline submit error instead of corner toast.
    setSubmitError: assign(setSubmitError),
    clearSubmitError: assign(clearSubmitError),
    // [MCV-875] Duplicate rejection is a client-side guard, not an API error,
    // so use a specific toast message instead of the generic Axios error alert.
    sendDuplicateError: ({ system }) =>
      sendCustomError({
        system,
        text: 'Area of interest already added',
        subText: 'This area of interest is already in your list.',
      }),
  },
  guards: {
    // [MCV-875] Reject a submit that would add an AOI already in the provider's list.
    isDuplicateAoi: ({ context, event }) => {
      const submit = event as SubmitAOIEvent;
      const submittedId = submit.values?.aoiSelect?.value;
      if (!submittedId) return false;
      return context.aois.some((aoi) => aoi.id.toString() === submittedId);
    },
  },
}).createMachine({
  context: ({ input, self }) => ({
    loading: false,
    pagination: {
      page: 1,
      pageSize: 5,
      total: 0,
    },
    aois: input.aois || [],
    idToDelete: -1,
    providerId: input.providerId || -1,
    AOISpecialtyOptions: [],
    AOIOptions: [],
    specialtiesId: '',
    providerCategoryId: input.providerCategoryId || -1,
    formValues: emptyForm,
    requestProvider:
      input?.requestProvider ||
      (() => {
        throw new Error('No request provider configured');
      }),
    modal: false,
    _aoiBuffer: undefined,
    submitError: undefined,
  }),
  initial: '$_AOI_DETAILS',
  on: {
    PROVIDER_RESPONSE: {
      actions: { type: 'setProviderIds' },
    },
    ON_LOAD: {
      target: '.$_GET',
      actions: ['setLoadingTrue'],
    },
    ON_SET_PROVIDER_IDS: {
      actions: { type: 'setProviderIds' },
    },
    ON_GET_SPEC_OPTIONS: {
      target: '.$_GET_SPEC_OPTIONS',
    },
    ON_SET_SPECIALTIES_ID: {
      actions: { type: 'setSpecialtiesId' },
      target: '.$_GET_AOI_OPTIONS',
    },
    ON_OPEN: {
      actions: [{ type: 'toggleModal' }, { type: 'clearSubmitError' }],
      target: '.$_AIO_FORM',
    },
    ON_CLOSE: {
      actions: [{ type: 'toggleModal' }, { type: 'clearSubmitError' }],
      target: '.$_AOI_DETAILS',
    },
  },

  states: {
    $_AOI_DETAILS: {
      on: {
        ON_ADD: {
          actions: ['toggleModal'],
          target: '$_AIO_FORM',
        },
        ON_SEND_DELETE_CONFIRMATION: {
          actions: ['openConfirmation'],
        },
        ON_DELETE: {
          actions: ['setIdToDelete', 'setLoadingTrue'],
          target: '$_SEND_DELETE_AOI',
        },
        ON_GET: {
          actions: ['setLoadingTrue'],
          target: `$_GET`,
        },
      },
    },
    $_AIO_FORM: {
      on: {
        ON_SUBMIT: [
          // [MCV-875] Reject duplicates before hitting the API.
          {
            guard: 'isDuplicateAoi',
            actions: ['sendDuplicateError'],
          },
          // [CQ3] Stage buffer via assign action, then invoke create actor.
          {
            actions: ['setLoadingTrue', 'setAoiBuffer', 'clearSubmitError'],
            target: '$_SEND_AOI',
          },
        ],
      },
    },
    $_SEND_AOI: {
      invoke: {
        id: 'create',
        src: 'create',
        input: (inputProps) => inputProps,
        onDone: {
          target: '$_GET',
          actions: ['add', 'toggleModal'],
        },
        // [MCV-873] Keep the form open on API failure so the user's selections are preserved.
        // [MCV-1207] Surface the error inline in the form, not as a corner toast.
        // setSubmitError also sets loading=false so we don't need setLoadingFalse here.
        onError: {
          actions: ['setSubmitError'],
          target: '$_AIO_FORM',
        },
      },
    },
    $_GET: {
      invoke: {
        id: 'get',
        src: 'get',
        input: (inputProps) => inputProps,
        onDone: {
          actions: ['setAois', 'setLoadingFalse'],
          target: '$_AOI_DETAILS',
        },
        onError: {
          actions: ['sendError', 'setLoadingFalse'],
          target: '$_AOI_DETAILS',
        },
      },
    },
    $_SEND_DELETE_AOI: {
      invoke: {
        id: 'delete',
        src: 'delete',
        input: (inputProps) => inputProps,
        onDone: {
          actions: ['delete', 'setLoadingFalse'],
          target: '$_AOI_DETAILS',
        },
        onError: {
          target: '$_AOI_DETAILS',
          actions: ['sendError', 'setLoadingFalse'],
        },
      },
    },
    $_GET_SPEC_OPTIONS: {
      invoke: {
        id: 'getSpecOptions',
        src: 'getSpecOptions',
        input: (inputProps) => inputProps,
        onDone: {
          target: '$_AIO_FORM',
          actions: ['setSpecOptions', 'setLoadingFalse'],
        },
        onError: {
          target: '$_AIO_FORM',
          actions: ['sendError', 'setLoadingFalse'],
        },
      },
    },
    $_GET_AOI_OPTIONS: {
      invoke: {
        id: 'getAOIOptions',
        src: 'getAOIOptions',
        input: (inputProps) => inputProps,
        onDone: {
          actions: ['setAOIOptions', 'setLoadingFalse'],
          target: '$_AIO_FORM',
        },
        onError: {
          actions: ['sendError', 'setLoadingFalse'],
          target: '$_AIO_FORM',
        },
      },
    },
  },
});
